// チェック関係関数ライブラリ
import * as moment from 'moment'
import * as _ from 'lodash'
import { convertElaYearToGregorianYear } from '@/util/common'

/**
 * メールアドレスフォーマットチェック
 * @param {string} string メールアドレス 
 * @return 正しい場合はtrue、そうでない場合はfalse
 */
export const checkEmailFormat = (string) => {
  if (string === '') return true // 必須チェックは行わないので空の場合はtrueを返す
  const regex = /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/
  if (regex.test(string)) {
    return true
  } else {
    return false
  }
}

/**
 * 電話番号フォーマットチェック
 * 総務省の固定電話番号の定義を参照。
 * https://www.soumu.go.jp/main_sosiki/joho_tsusin/top/tel_number/number_shitei.html
 * 固定電話番号と携帯番号の条件も満たす条件。
 * ただし、フリーダイヤルはハイフンの位置を自由に決められるため、考慮する。
 * http://www.fujipro-inc.com/2017/07/31/5015.html
 * （LC APIのバリデーションと一致させている）
 * @param {*} string 
 */
export const checkTelFormat = (string) => {
  const regex1 = /^0[0-9]{1,4}-[0-9]{1,6}(-[0-9]{0,5})?$/
  const regex2 = /^[0-9]{10,11}$/
  const string2 = string.replace(/-/g, '')
  if (!regex1.test(string) || !regex2.test(string2)) {
    return false
  }
  if (string.length <= 13) {
    return true
  } else {
    return false
  }
}

/**
 * 内線番号フォーマットチェック
 * 半角数字・ハイフン5桁以内
 * @param {*} string 
 */
export const checkExtensionFormat = (string) => {
  const regex = /^[0-9-]{1,5}$/
  if (!regex.test(string)) {
    return false
  }
  if (string.length > 5) {
    return false
  }
  return true
}

/**
 * 文字列の最小長チェック
 * @param {string} string 対象文字列
 * @param {integer} minLength 文字列最小長
 * @return 最小長以上であればtrue、最小長未満であればfalse
 */
export const checkMinLength = (string, minLength) => {
  if (string.length >= minLength) {
    return true
  } else {
    return false
  }
}

/**
 * 文字列の最大長チェック
 * @param {string} string 対象文字列
 * @param {integer} maxLength 文字列最大長
 * @return 最大長以内であればtrue、最大長を超えたらfalse
 */
export const checkMaxLength = (string, maxLength) => {
  if (string.length <= maxLength) {
    return true
  } else {
    return false
  }
}

/**
 * 文字列長チェック
 * @param {string} string 対象文字列
 * @param {integer} maxLength 文字列長
 * @return 指定の文字列長であればtrue、そうでなければfalse
 */
export const checkLength = (string, length) => {
  if (string.length === length) {
    return true
  } else {
    return false
  }
}

/**
 * 全ての文字が半角文字であるかチェック(空文字列はOK)
 * @param {} string 
 */
export const checkHankaku = (string) => {
  if (string == null) string = ''
  const regex = /^[\x20-\x7e]*$/
  const result = regex.test(string)
  return result
}

/**
 * 全ての文字が全角文字であるかチェック(空文字列はOK)
 * @param {} string 
 */
export const checkZenkaku = (string) => {
  if (string == null) string = ''
  const regex = /^[^\x01-\x7E\xA1-\xDF]*$/
  const result = regex.test(string) 
  return result
}

/**
 * 全ての文字が全角ひらがなであるかチェック(空文字列はOK)
 * @param {} string 
 */
export const checkZenkakuHiragana = (string) => {
  if (string == null) string = ''
  // \u3041-\u3096: 全角ひらがな
  // \u3000: 全角スペース
  const regex = /^[\u3041-\u3096\u3000]*$/
  const result = regex.test(string)
  return result
}

/**
 * 全ての文字が全角カタカナであるかチェック(空文字列はOK)
 * @param {} string 
 */
export const checkZenkakuKatakana = (string) => {
  if (string == null) string = ''
  // \u30a1-\u30f6: 全角カタカナ
  // \u30fc: 全角の長音
  // \u3000: 全角スペース
  const regex = /^[\u30a1-\u30f6\u30fc\u3000]*$/
  const result = regex.test(string)
  return result
}

/**
 * 全ての文字が半角英数字であるかチェック(空文字列はOK)
 * @param {} string 
 */
export const checkHankakuAlphabetNumber = (string) => {
  if (string == null) string = ''
  const regex = /^[0-9a-zA-Z]*$/
  const result = regex.test(string)
  return result
}

/**
 * 全ての文字が半角数字であるかチェック(空文字列はOK)
 * @param {} string 
 */
export const checkHankakuNumber = (string) => {
  if (string == null) string = ''
  const regex = /^[0-9]*$/
  const result = regex.test(string)
  return result
}

/**
 * 全ての文字が半角英字であるかチェック(空文字列はOK)
 * @param {} string 
 */
export const checkHankakuAlphabet = (string) => {
  if (string == null) string = ''
  const regex = /^[a-zA-Z]*$/
  const result = regex.test(string)
  return result
}

/**
 * 日付の妥当性チェック
 * @param {number} year1
 * @param {number} month1 
 * @param {number} mday1 
 */
export const checkDate = (year1, month1, mday1) => {
  if (!_.isNumber(year1) || !_.isNumber(month1) || !_.isNumber(mday1)) {
    return false
  }
  const isValid = moment([year1, month1 - 1, mday1]).isValid()
  return isValid
}

/**
 * 元号、年の妥当性チェック
 * @param {*} era 
 * @param {*} year 
 */
export const checkEraYear = (era, year) => {
  if (era === '明治') {
    // 明治6年1月1日 ～ 明治45年（1912年）7月29日
    // ※次の理由から、明治は6年1月1日以降を指定可能とする。
    // ・グレゴリオ暦は明治6年1月1日から採用された。
    // ・グレゴリオ暦移行の関係で明治5年は、12月3日から12月30日までの28日間が存在しない。
    if (year >= 6 && year <= 45) {
      return true
    }
  } else if (era === '大正') {
    // 大正元年（1912年）7月30日 ～ 大正15年（1926年）12月24日
    if (year >= 1 && year <= 15) {
      return true
    }
  } else if (era === '昭和') {
    // 昭和元年（1926年）12月25日 ～ 昭和64年（1989年）1月7日
    if (year >= 1 && year <= 64) {
      return true
    }
  } else if (era === '平成') {
    // 平成元年（1989年）1月8日 ～ 平成31年（2019年）4月30日
    if (year >= 1 && year <= 31) {
      return true
    }
  } else if (era === '令和') {
    // 令和元年（2019年）5月1日 ～ 今日
    const currentEraYear = (new Date()).getFullYear() - 2018
    if (year >= 1 && year <= currentEraYear) {
      return true
    }
  }
  return false
}

/**
 * 元号、年、月の妥当性チェック
 * @param {*} era 
 * @param {*} year 
 */
export const checkEraYearMonth = (era, year, month) => {
  // 元号、年の妥当性チェック
  const isOK = checkEraYear(era, year)
  if (isOK) {
    // 元号、年の妥当性チェックがOKの場合、最終年の月の上限チェックをする
    if (era === '明治') {
      // 明治6年1月1日 ～ 明治45年（1912年）7月29日
      // ※次の理由から、明治は6年1月1日以降を指定可能とする。
      // ・グレゴリオ暦は明治6年1月1日から採用された。
      // ・グレゴリオ暦移行の関係で明治5年は、12月3日から12月30日までの28日間が存在しない。
      if (year === 1) {
        if (month >= 1 && month <= 12) {
          return true
        }
      } else if (year === 45) {
        if (month >= 1 && month <= 7) {
          return true
        }
      } else {
        if (month >= 1 && month <= 12) {
          return true
        }
      }
    } else if (era === '大正') {
      // 大正元年（1912年）7月30日 ～ 大正15年（1926年）12月24日
      if (year === 1) {
        if (month >= 7 && month <= 12) {
          return true
        }
      } else if (year === 15) {
        if (month >= 1 && month <= 12) {
          return true
        }
      } else {
        if (month >= 1 && month <= 12) {
          return true
        }
      }
    } else if (era === '昭和') {
      // 昭和元年（1926年）12月25日 ～ 昭和64年（1989年）1月7日
      if (year === 1) {
        if (month === 12) {
          return true
        }
      } else if (year === 64) {
        if (month === 1) {
          return true
        }
      } else {
        if (month >= 1 && month <= 12) {
          return true
        }
      }
    } else if (era === '平成') {
      // 平成元年（1989年）1月8日 ～ 平成31年（2019年）4月30日
      if (year === 1) {
        if (month >= 1 && month <= 12) {
          return true
        }
      } else if (year === 31) {
        if (month >= 1 && month <= 4) {
          return true
        }
      } else {
        if (month >= 1 && month <= 12) {
          return true
        }
      }
    } else if (era === '令和') {
      // 令和元年（2019年）5月1日 ～ 今日
      const currentEraYear = (new Date()).getFullYear() - 2018
      const currentMonth = (new Date()).getMonth() + 1
      if (year === 1) {
        if (month >= 5 && month <= 12) {
          return true
        }
      } else if (year === currentEraYear) {
        if (month >= 1 && month <= currentMonth) {
          return true
        }
      } else {
        if (month >= 1 && month <= 12) {
          return true
        }
      }
    }
  
  }
  return false
}

/**
 * 元号、年、月、日の妥当性チェック
 * @param {*} era 
 * @param {*} year 
 * @param {*} month 
 * @param {*} mday 
 */
export const checkEraYearMonthMday = (era, year, month, mday) => {
  // 元号、年、月の妥当性チェック
  const isOK = checkEraYearMonth(era, year, month)
  if (isOK) {
    // 元号、年の妥当性チェックがOKの場合、最終年の月の上限チェックをする
    if (era === '明治') {
      // 明治6年1月1日 ～ 明治45年（1912年）7月29日
      // ※次の理由から、明治は6年1月1日以降を指定可能とする。
      // ・グレゴリオ暦は明治6年1月1日から採用された。
      // ・グレゴリオ暦移行の関係で明治5年は、12月3日から12月30日までの28日間が存在しない。
      if (year === 45 && month === 7) {
        // 最終年月の場合は日の範囲チェック
        if (mday >= 1 && mday <= 29) {
          return true
        }
      } else {
        const gregorianYear = convertElaYearToGregorianYear(era, year)
        const isOK = checkDate(gregorianYear, month, mday)
        if (isOK) return true
      }
    } else if (era === '大正') {
      // 大正元年（1912年）7月30日 ～ 大正15年（1926年）12月24日
      if (year === 1 && month === 7) {
        // 開始年月の場合は日の範囲チェック
        if (mday >= 30 && mday <= 31) {
          return true
        }
      } else if (year === 15 && month === 12) {
        // 最終年月の場合は日の範囲チェック
        if (mday >= 1 && mday <= 24) {
          return true
        }
      } else {
        const gregorianYear = convertElaYearToGregorianYear(era, year)
        const isOK = checkDate(gregorianYear, month, mday)
        if (isOK) return true
      }
    } else if (era === '昭和') {
      // 昭和元年（1926年）12月25日 ～ 昭和64年（1989年）1月7日
      if (year === 1 && month === 12) {
        // 開始年月の場合は日の範囲チェック
        if (mday >= 25 && mday <= 31) {
          return true
        }
      } else if (year === 64 && month === 1) {
        // 最終年月の場合は日の範囲チェック
        if (mday >= 1 && mday <= 7) {
          return true
        }
      } else {
        const gregorianYear = convertElaYearToGregorianYear(era, year)
        const isOK = checkDate(gregorianYear, month, mday)
        if (isOK) return true
      }
    } else if (era === '平成') {
      // 平成元年（1989年）1月8日 ～ 平成31年（2019年）4月30日
      if (year === 1 && month === 1) {
        // 開始年月の場合は日の範囲チェック
        if (mday >= 8 && mday <= 31) {
          return true
        }
      } else if (year === 31 && month === 4) {
        // 最終年月の場合は日の範囲チェック
        if (mday >= 1 && mday <= 30) {
          return true
        }
      } else {
        const gregorianYear = convertElaYearToGregorianYear(era, year)
        const isOK = checkDate(gregorianYear, month, mday)
        if (isOK) return true
      }
    } else if (era === '令和') {
      // 令和元年（2019年）5月1日 ～ 今日
      const currentEraYear = (new Date()).getFullYear() - 2018
      const currentMonth = (new Date()).getMonth() + 1
      const currentMday = (new Date()).getDate()
      if (year === 1 && month === 5) {
        // 開始年月の場合は日の範囲チェック
        if (mday >= 1 && mday <= 31) {
          return true
        }
      } else if (year === currentEraYear && month === currentMonth) {
        if (mday >= 1 && mday <= currentMday) {
          return true
        }
      } else {
        const gregorianYear = convertElaYearToGregorianYear(era, year)
        const isOK = checkDate(gregorianYear, month, mday)
        if (isOK) return true
      }
    }
  
  }
  return false
}
